import { useCallback, useEffect, useRef } from 'react';
/**
 * Registers close callbacks that fire when the user clicks inside an iframe.
 *
 * Iframe clicks don't bubble to the parent document, but they *do* cause
 * the parent window to lose focus, which we detect via the `blur` event.
 *
 * Usage:
 *
 * ```ts
 * const { registerCloser } = useCloseOnIframeClick();
 * useEffect(() => registerCloser(() => setIsShareOpen(false)), [registerCloser]);
 * useEffect(() => registerCloser(() => setIsDownloadOpen(false)), [registerCloser]);
 * ```
 */
export function useCloseOnIframeClick() {
    const closers = useRef(new Set());
    useEffect(() => {
        let pendingTimeoutId;
        const handleWindowBlur = () => {
            if (pendingTimeoutId) {
                window.clearTimeout(pendingTimeoutId);
            }
            // Browser focus updates to the iframe after the blur handler runs.
            // Defer one task so ordinary window blurs are not treated as iframe clicks.
            pendingTimeoutId = window.setTimeout(() => {
                pendingTimeoutId = undefined;
                if (document.activeElement?.tagName !== 'IFRAME') {
                    return;
                }
                closers.current.forEach((close) => close());
            }, 0);
        };
        window.addEventListener('blur', handleWindowBlur);
        return () => {
            window.removeEventListener('blur', handleWindowBlur);
            if (pendingTimeoutId) {
                window.clearTimeout(pendingTimeoutId);
            }
        };
    }, []);
    const registerCloser = useCallback((close) => {
        closers.current.add(close);
        return () => {
            closers.current.delete(close);
        };
    }, []);
    return { registerCloser };
}
//# sourceMappingURL=useCloseOnIframeClick.js.map